import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// Tokens live in backend-issued httpOnly cookies; we never read or write them
// in JS. Frontend state is just the current user object derived from /auth/me.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);    // null = checking, false = anon, object = signed in
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      // 401 is the normal anonymous path; nothing to surface to the user here.
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, skip /me check — AuthCallback will handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refreshMe();
  }, [refreshMe]);

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const registerEmail = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const exchangeGoogleSession = async (session_id, role = "client") => {
    const { data } = await api.post("/auth/google/session", { session_id, role });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // logout is best-effort; ignore network errors
    }
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, loading, loginEmail, registerEmail, exchangeGoogleSession, logout, refreshMe, formatApiError }),
    [user, loading, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
